import { useCallback, useEffect, useRef, useState } from "react";

const prefix = "cchat:composer-draft:v1:";
export const composerDraftKey = ({ companyId, userId, ticketId, ticketUuid, ready, privateMessage }) => {
  if (!ready || !companyId || !userId) return null;
  const identity = ticketUuid && ticketUuid !== "undefined"
    ? ["uuid", String(ticketUuid)]
    : ticketId ? ["id", String(ticketId)] : null;
  return identity ? prefix + JSON.stringify([String(companyId), String(userId), ...identity, privateMessage ? "private" : "message"]) : null;
};

// Drafts belong to this browser tab/session. There is no request or database write.
// Memory fallback keeps ticket switching usable if browser storage is unavailable.
export default function useComposerDraft(key, temporary = false) {
  const cache = useRef(new Map());
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const read = useCallback(draftKey => {
    if (!draftKey) return "";
    if (cache.current.has(draftKey)) return cache.current.get(draftKey);
    let text = "";
    try { text = sessionStorage.getItem(draftKey) || ""; } catch { /* Use memory only. */ }
    cache.current.set(draftKey, text);
    return text;
  }, []);
  const write = useCallback((draftKey, text) => {
    if (!draftKey) return;
    cache.current.set(draftKey, text);
    try {
      if (text) sessionStorage.setItem(draftKey, text);
      else sessionStorage.removeItem(draftKey);
    } catch { /* Quota/privacy settings must not interrupt typing. */ }
  }, []);
  const [state, setState] = useState(() => ({ key, temporary, text: read(key) }));
  const text = !key ? "" : temporary && state.key === key && state.temporary ? state.text : read(key);
  const active = useRef();
  active.current = { key, temporary, text };
  const setText = useCallback(value => {
    if (!key || !mounted.current) return;
    const current = active.current;
    const isCurrent = current.key === key && current.temporary === temporary;
    const previous = isCurrent ? current.text : read(key);
    const next = typeof value === "function" ? value(previous) : value;
    if (!temporary) write(key, next);
    if (!isCurrent || !mounted.current) return;
    const updated = { key, temporary, text: next };
    active.current = updated;
    setState(updated);
  }, [key, temporary, read, write]);
  const clearSentDraft = useCallback((sentKey, sentText) => {
    if (!sentKey) return;
    // Another instance may have restored this ticket and changed its draft.
    // Never let the old instance's cache erase that newer browser value.
    if (!mounted.current) {
      try { if ((sessionStorage.getItem(sentKey) || "") !== sentText) return; }
      catch { return; }
    }
    if (read(sentKey) !== sentText) return;
    write(sentKey, "");
    const current = active.current;
    if (mounted.current && current.key === sentKey && !current.temporary && current.text === sentText) {
      const updated = { ...current, text: "" };
      active.current = updated;
      setState(updated);
    }
  }, [read, write]);
  return [text, setText, clearSentDraft];
}
